/**
 * Resolution helpers for admin-defined "Specification Configs".
 *
 * A product line with no configured presets falls back to the three cards the wizard has always
 * shown, so the frontend can ship before any tier is configured in the admin panel.
 */

import type {
  OSImage,
  ProductLineDto,
  ProductLineSpecConfig,
  ProductLineSpecLimits,
  ProductLineSpecRange,
} from "@/types/osImage";

/**
 * Slider bounds for every axis, exactly as the API resolved them.
 *
 * There is deliberately no client-side copy of the platform limits: the catalog API always returns
 * a complete `specLimits` (admin-configured axes, the rest filled from ProductLineSpecBounds), so
 * a hardcoded fallback here could only ever drift from the real ceiling the backend enforces.
 */
export type ResolvedSpecLimits = Record<
  keyof ProductLineSpecLimits,
  ProductLineSpecRange
>;

/**
 * Admin-defined presets for a product line, or an empty list when none are configured.
 * There is intentionally no hardcoded fallback — presets are admin-managed data, so an
 * unconfigured product line shows no preset cards rather than fabricated ones.
 */
export function resolveSpecConfigs(
  line: ProductLineDto | null | undefined,
): ProductLineSpecConfig[] {
  return line?.specConfigs ?? [];
}

/** Preferred initial tier: Cheap, then Eco, then the first API-ordered product line. */
export function resolveDefaultProductLine(
  productLines: ProductLineDto[],
): ProductLineDto | undefined {
  const matchesTier = (line: ProductLineDto, tier: "cheap" | "eco") =>
    line.tier.trim().toLowerCase() === tier ||
    line.slug.trim().toLowerCase() === tier;

  return (
    productLines.find((line) => matchesTier(line, "cheap")) ??
    productLines.find((line) => matchesTier(line, "eco")) ??
    productLines[0]
  );
}

/**
 * Slider bounds for a product line as served by the API, or null when they are not available yet
 * (no tier selected, catalog still loading). Callers must not invent bounds for the null case —
 * render a pending state instead, so the wizard can never offer a range the backend would reject.
 */
export function resolveSpecLimits(
  line: ProductLineDto | null | undefined,
): ResolvedSpecLimits | null {
  const limits = line?.specLimits;
  if (
    !limits?.cores ||
    !limits.memoryGb ||
    !limits.diskGb ||
    !limits.ips ||
    !limits.backupCount ||
    !limits.snapshotCount
  ) {
    return null;
  }

  return {
    cores: limits.cores,
    memoryGb: limits.memoryGb,
    diskGb: limits.diskGb,
    ips: limits.ips,
    backupCount: limits.backupCount,
    snapshotCount: limits.snapshotCount,
  };
}

/** The preset applied when a tier is selected. */
export function resolveDefaultConfig(
  line: ProductLineDto | null | undefined,
): ProductLineSpecConfig | undefined {
  const configs = resolveSpecConfigs(line);
  return configs.find((c) => c.isDefault) ?? configs[0];
}

/**
 * Picks a label for the active language: exact match, then the base language ("vi-VN" -> "vi"),
 * then English, then any value present.
 */
export function resolveLocalizedText(
  map: Record<string, string> | undefined,
  language: string,
): string | undefined {
  if (!map) return undefined;
  const base = language.split("-")[0]?.toLowerCase() ?? "en";
  return (
    map[language] ?? map[base] ?? map.en ?? Object.values(map).find(Boolean)
  );
}

/**
 * Raises the tier's slider floors to what the selected OS image needs to run.
 *
 * The tier says what it SELLS; the image says what it needs to RUN. Checkout refuses an order below
 * the image's own minimum (BELOW_TEMPLATE_MINIMUM_SPECS), and raising the floors here keeps that
 * refusal off the last step of the wizard.
 *
 * Raised, never lowered: an image asking for less than the tier sells does not unlock a machine
 * smaller than the tier offers. Never past the tier's own maximum — a floor above the ceiling would
 * invert the slider, so it collapses onto the maximum instead.
 *
 * The raised floor is rounded UP onto the tier's step grid. An image minimum is stored in MB and
 * arrives as a fractional GB (AlmaLinux and Rocky need 1536 MB = 1.5 GB); used as the floor as-is,
 * it moved the whole grid, because `clampSpec` snaps relative to `min`: every preset re-snapped to
 * x.5 GB (16 GB -> 16.5 GB), so no preset card could ever match the specs, and the customer was
 * quoted and billed for half a GB they never picked.
 */
export function raiseSpecLimitsToImageMinimums(
  limits: ResolvedSpecLimits | null,
  minimums: { cores: number; memory: number; disk: number } | null | undefined,
): ResolvedSpecLimits | null {
  if (!limits || !minimums) return limits;

  const raise = (range: ProductLineSpecRange, floor: number | undefined) => {
    if (floor === undefined || !Number.isFinite(floor) || floor <= range.min) {
      return range;
    }
    const step = Math.max(1, range.step);
    const onGrid = range.min + Math.ceil((floor - range.min) / step) * step;
    return { ...range, min: Math.min(onGrid, range.max) };
  };

  return {
    ...limits,
    cores: raise(limits.cores, minimums.cores),
    memoryGb: raise(limits.memoryGb, minimums.memory),
    diskGb: raise(limits.diskGb, minimums.disk),
  };
}

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * What a selection fell short of: the image's name and the minimums it declares, memory in GB. An
 * axis the image leaves undeclared (0 in the catalogue) is absent, so no message reads "at least
 * 0 CPU".
 */
export type ImageMinimumShortfall = {
  os: string;
  cores?: number;
  memory?: number;
  disk?: number;
};

/**
 * Reports a selection smaller than the OS image needs to run, or null when it fits — the frontend
 * twin of the backend's BELOW_TEMPLATE_MINIMUM_SPECS gate (`TemplateMinimumSpecs.FindShortfalls`),
 * which refuses the paid order and the trial alike on these three axes.
 *
 * Measured against the image's own minimums, not the slider floors: `raiseSpecLimitsToImageMinimums`
 * caps a floor at the tier's maximum, so on a tier whose ceiling sits below what the image needs the
 * sliders cannot show the shortfall. An axis the selection leaves unset, or the image declares as 0
 * (the catalogue's "not declared"), is not measured — the same as the backend.
 */
export function findImageMinimumShortfall(
  specs: Pick<SpecSelection, "cores" | "memory" | "disk">,
  image: Pick<OSImage, "name" | "minimumSpecs"> | null | undefined,
): ImageMinimumShortfall | null {
  const minimums = image?.minimumSpecs;
  if (!image || !minimums) return null;

  const below = (value: number | undefined, minimum: number) =>
    value !== undefined && minimum > 0 && value < minimum;

  const fits =
    !below(specs.cores, minimums.cores) &&
    !below(specs.memory, minimums.memory) &&
    !below(specs.disk, minimums.disk);
  if (fits) return null;

  const shortfall: ImageMinimumShortfall = { os: image.name };
  if (minimums.cores > 0) shortfall.cores = minimums.cores;
  if (minimums.memory > 0) shortfall.memory = minimums.memory;
  if (minimums.disk > 0) shortfall.disk = minimums.disk;
  return shortfall;
}

/**
 * The customer-facing sentence for a shortfall (`wizard.errors.belowOsMinimum`), listing only the
 * axes the image declares.
 */
export function formatImageMinimumShortfall(
  shortfall: ImageMinimumShortfall,
  t: TFunction,
): string {
  const requirements: string[] = [];
  if (shortfall.cores !== undefined) {
    requirements.push(
      t("wizard.errors.osMinimumCores", { value: shortfall.cores }),
    );
  }
  if (shortfall.memory !== undefined) {
    requirements.push(
      t("wizard.errors.osMinimumMemory", { value: shortfall.memory }),
    );
  }
  if (shortfall.disk !== undefined) {
    requirements.push(
      t("wizard.errors.osMinimumDisk", { value: shortfall.disk }),
    );
  }

  return t("wizard.errors.belowOsMinimum", {
    os: shortfall.os,
    requirements: requirements.join(" • "),
  });
}

/** Clamps a spec value into a range, snapping to the range's step. */
export function clampSpec(value: number, range: ProductLineSpecRange): number {
  const step = Math.max(1, range.step);
  const snapped = range.min + Math.round((value - range.min) / step) * step;
  return Math.min(range.max, Math.max(range.min, snapped));
}

/** The specification axes an order carries, in the order the wizard presents them. */
export interface SpecSelection {
  cores?: number;
  /** RAM in GB, matching the slider rather than the backend's MB. */
  memory?: number;
  disk?: number;
  additionalIps?: number;
  backupCount?: number;
  snapshotCount?: number;
}

/**
 * Reports the first axis that falls outside the product line's configured range, or null when the
 * selection is acceptable.
 *
 * The sliders already clamp, so this only fires when a selection reaches the submit step without
 * having passed through them — a persisted wizard state from a previous session, or a product line
 * whose limits were tightened in the admin panel after the customer started. Clamping cannot be the
 * answer at that point: silently repricing a VM the customer never configured is exactly what the
 * backend's ProductLineSpecValidation refuses to do, so the frontend stops with the same complaint
 * instead of sending an order the API would reject anyway.
 *
 * Axis order mirrors ProductLineSpecValidation.TryValidate so both sides name the same control.
 */
export function findSpecLimitViolation(
  specs: SpecSelection,
  limits: ResolvedSpecLimits | null | undefined,
): {
  axis: keyof ResolvedSpecLimits;
  value: number;
  range: ProductLineSpecRange;
} | null {
  if (!limits) return null;

  const axes: Array<[keyof ResolvedSpecLimits, number | undefined]> = [
    ["cores", specs.cores],
    ["memoryGb", specs.memory],
    ["diskGb", specs.disk],
    ["ips", specs.additionalIps],
    ["backupCount", specs.backupCount],
    ["snapshotCount", specs.snapshotCount],
  ];

  for (const [axis, value] of axes) {
    // An omitted axis is "not chosen by this request" and is left to the backend's defaults,
    // matching how TryValidate treats a null.
    if (value === undefined || value === null) continue;

    const range = limits[axis];
    if (value < range.min || value > range.max) {
      return { axis, value, range };
    }
  }

  return null;
}
